import { isEmpty } from 'lodash'
import { AggregationPipeline } from 'types/common/aggregation'
import { AuthRoleIdEnum } from 'constants/user'
import { ESortingUserOptions } from '../UserListFilterDialog/constants'

export function getUsersFilterPipeline(
  organizationId: number,
  limit: number,
  skip: number,
  keyword: string,
  isViewer?: boolean,
  sortBy?: string,
  roleIds?: number[],
  groupIds?: number[],
  statuses?: string[]
): AggregationPipeline {
  let sort: any = {
    updatedAt: -1
  }
  if (sortBy) {
    switch (sortBy) {
      case ESortingUserOptions.NAME:
        sort = {
          firstName: 1,
          lastName: 1
        }
        break
      case ESortingUserOptions.NEWEST:
        sort = {
          updatedAt: -1
        }
        break
      case ESortingUserOptions.LAST_LOGIN:
        sort = {
          lastSignInAt: -1
        }
        break
    }
  }

  const pipeline: AggregationPipeline = [
    {
      $addFields: {
        fullName: {
          $concat: ['$firstName', ' ', '$lastName']
        }
      }
    },
    {
      $match: {
        $or: [
          { firstName: { $regex: `.*${keyword}.*`, $options: 'i' } },
          { lastName: { $regex: `.*${keyword}.*`, $options: 'i' } },
          { email: { $regex: `.*${keyword}.*`, $options: 'i' } },
          { username: { $regex: `.*${keyword}.*`, $options: 'i' } },
          { fullName: { $regex: `.*${keyword}.*`, $options: 'i' } }
        ]
      }
    },
    {
      $lookup: {
        from: 'GroupMember',
        localField: '_id',
        foreignField: 'memberId',
        as: 'groupMembers'
      }
    },
    {
      $lookup: {
        from: 'Group',
        localField: 'groupMembers.groupId',
        foreignField: '_id',
        as: 'groups'
      }
    },
    {
      $lookup: {
        from: 'AuthRole',
        localField: 'authRoleId',
        foreignField: '_id',
        as: 'authRole'
      }
    },
    {
      $unwind: {
        path: '$authRole'
      }
    }
  ]

  if (isViewer) {
    pipeline.unshift({
      $match: {
        authRoleId: {
          $ne: AuthRoleIdEnum.BASIC_USER
        }
      }
    })
  }

  if (!isEmpty(roleIds)) {
    pipeline.push({
      $match: {
        authRoleId: {
          $in: roleIds
        }
      }
    })
  }

  if (!isEmpty(groupIds)) {
    pipeline.push({
      $match: {
        'groupMembers.groupId': {
          $in: groupIds
        }
      }
    })
  }

  const statusFilters = (statuses ?? []).filter(Boolean)
  const includeActive = statusFilters.includes('active')
  const includeDisabled = statusFilters.includes('disabled')
  const includeDeleted = statusFilters.includes('deleted')

  const baseMatch: any = {
    organizationId
  }

  if (includeActive || includeDisabled || includeDeleted) {
    const orConditions: any[] = []

    if (includeActive) {
      orConditions.push({
        isInactive: { $ne: true },
        disabled: { $ne: true }
      })
    }

    if (includeDisabled) {
      orConditions.push({
        disabled: true
      })
    }

    if (includeDeleted) {
      orConditions.push({
        isInactive: true
      })
    }

    baseMatch.$or = orConditions
  }

  pipeline.unshift({
    $match: baseMatch
  })

  const paginationPipeline: AggregationPipeline = [
    {
      $sort: sort
    },
    {
      $facet: {
        results: [
          {
            $skip: skip
          },
          {
            $limit: limit
          },
          {
            $project: {
              _id: false,
              id: '$_id',
              createdAt: true,
              updatedAt: true,
              encryptedPassword: true,
              lastSignInAt: true,
              firstName: true,
              lastName: true,
              username: true,
              image: true,
              email: true,
              ssoEmail: true,
              ssoEmailVerified: true,
              ssoEmailBoundAt: true,
              ssoGraceResetAt: true,
              ssoLifecycleAutoActionAt: true,
              authMethodOverride: true,
              isInactive: true,
              disabled: true,
              organizationId: true,
              authRoleId: true,
              groupMembers: true,
              groups: true,
              authRole: true
            }
          }
        ],
        totalCount: [
          {
            $count: 'total'
          }
        ]
      }
    }
  ]
  pipeline.push(...paginationPipeline)

  return pipeline
}
